import React, { FC, useState, useEffect } from 'react'
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/outline'
import { useLocation } from 'react-router-dom'
import parser from 'query-string'
import { Heading, Button } from '../System'
import { useCourseRegisterHandler } from '../Auth'
import { ReactComponent as WaveSvg } from '../RegisterView/wave.svg'
import { CourseMarketing } from '../Auth/Marketing'
import { RegistrationLoader } from '../RegisterView/RegistrationLoader'
import { MainInfoStep } from '../RegisterView/Steps'
import { SubscriptionStep, PaymentStep } from './Steps'
import { FormMessage } from '../System/Form'
import { StyledCourseRegisterView } from '.'
import useAffiliateTrack from '../../redux/affiliate-track/useAffiliateTrack'

const CourseRegisterView: FC = () => {
  const {
    form,
    validation,
    loadingMessage,
    formError,
    actions: { handleRegister }
  } = useCourseRegisterHandler()
  const { actions: { setAffiliate, trackLinkClick } } = useAffiliateTrack()

  const STEPS = ['Main Information', 'Subscription', 'Payment', 'Complete Registration']
  const [loading, setLoading] = useState(false)
  const [currStep, setCurrStep] = useState(0)
  const { search } = useLocation()
  const { affiliate } = parser.parse(search)

  const nextStep = async () => {
    const isValid = await form.trigger()

    if (!isValid) {
      return
    }

    // Last step is decorative, so we want to submit
    // the form on the second-last step
    if (currStep === STEPS.length - 2) {
      setLoading(true)
      handleRegister()
      return
    }

    setCurrStep(currStep + 1)
  }

  useEffect(() => {
    if (affiliate && typeof affiliate === 'string') {
      setAffiliate(affiliate)
      trackLinkClick(affiliate)
    }
  }, [])

  return (
    <StyledCourseRegisterView currStep={currStep}>
      <CourseMarketing />
      <div id="form" className="auth-information">
        <div className="wave">
          <WaveSvg />
        </div>
        <div className="back-btn">
          {currStep > 0 && (
            <Button theme="ghost" isIcon onClick={() => setCurrStep(currStep - 1)}>
              <ChevronLeftIcon />
            </Button>
          )}
        </div>
        <div className="auth-form">
          <Heading>
            Purchase S.O.S.
            <br />
            Selling On Social
          </Heading>

          <div className="breadcrumbs">
            <b>{STEPS[currStep]}</b>
            {currStep !== STEPS.length - 1 && (
              <>
                <ChevronRightIcon />
                {STEPS[currStep + 1]}
              </>
            )}
          </div>

          {formError && <FormMessage message={formError} type="error" />}

          {currStep === 0 && (
            <MainInfoStep form={form} validation={validation} nextStep={() => nextStep()} />
          )}
          {currStep === 1 && <SubscriptionStep form={form} nextStep={() => nextStep()} />}
          {currStep === 2 && <PaymentStep form={form} nextStep={() => nextStep()} />}
          {loading && !formError && <RegistrationLoader currentProgress={loadingMessage} />}
        </div>
      </div>
    </StyledCourseRegisterView>
  )
}

export default CourseRegisterView
